/**
 * `/s/:token` — the buyer-facing page opened from a shared-Selection link.
 *
 * A sibling of `login.tsx`, never under `routes/_app/`: there is no session,
 * no offline machinery and no app shell here — a stranger on a phone opens a
 * link and must see something without an account. Data comes from a plain
 * `fetch` (never the authenticated `apiRequest`, which carries session/offline
 * semantics this page must not inherit).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import type { CreateQuoteRequestInput } from '@paradise/shared';
import { DEFAULT_QUOTATION_TERMS, ErrorEnvelope, SharedSelection } from '@paradise/shared';
import { z } from 'zod';

import logoUrl from '@/assets/logo-h.png';
import { QuoteRequestForm, type RequesterDetails } from '@/components/share/quote-request-form';
import { SharedItemCard } from '@/components/share/shared-item-card';
import {
  ALL_CATEGORIES,
  ANY_PRICE,
  matchesSearch,
  priceBandOf,
  SELECTED_ONLY,
  SharedItemFilters
} from '@/components/share/shared-item-filters';
import { ShortlistBar } from '@/components/share/shortlist-bar';
import { DocumentTerms } from '@/components/print/document-terms';
import { Card, CardContent } from '@/components/ui/card';
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty';
import { Spinner } from '@/components/ui/spinner';
import { scrollIntoViewStable } from '@/lib/scroll-into-view-stable';
import { useIncrementalList } from '@/lib/use-incremental-list';

/**
 * How many cards paint before the sentinel pulls the next page. Each carries a
 * ~800px photograph, so a 50-piece Selection previously asked a buyer on hotel
 * wifi to download all fifty before seeing the first.
 */
const PAGE_SIZE = 12;

export const Route = createFileRoute('/s/$token')({
  component: SharedSelectionPage
});

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ok'; data: SharedSelection }
  | { kind: 'expired' }
  | { kind: 'revoked' }
  | { kind: 'error' };

// `details` isn't part of the shared `ErrorBody` shape (it's the 410 route's
// own payload), so it's validated here rather than cast.
const GoneDetails = z.object({ reason: z.enum(['revoked', 'expired']) });

function goneReason(details: unknown): 'revoked' | 'expired' | null {
  const parsed = GoneDetails.safeParse(details);
  return parsed.success ? parsed.data.reason : null;
}

async function loadSelection(token: string): Promise<LoadState> {
  let response: Response;
  try {
    response = await fetch(`/api/share/${encodeURIComponent(token)}`);
  } catch {
    return { kind: 'error' };
  }

  if (response.status === 200) {
    const body: unknown = await response.json().catch(() => null);
    const parsed = SharedSelection.safeParse(body);
    return parsed.success ? { kind: 'ok', data: parsed.data } : { kind: 'error' };
  }

  if (response.status === 410) {
    const body: unknown = await response.json().catch(() => null);
    const envelope = ErrorEnvelope.safeParse(body);
    const reason = envelope.success ? goneReason(envelope.data.error.details) : null;
    return reason === 'expired' ? { kind: 'expired' } : { kind: 'revoked' };
  }

  return { kind: 'error' };
}

/**
 * itemNo -> what the buyer asked for. Presence in the map IS the tick — no
 * separate boolean.
 *
 * The stored shape used to be a bare quantity. `readShortlist` still accepts
 * that, because a buyer mid-browse when the app updates would otherwise lose
 * everything they had ticked.
 */
interface ShortlistLine {
  /**
   * NULL until the buyer types one, and they cannot send until they have.
   *
   * This used to pre-fill with the product's MOQ, which read as the buyer's own
   * word once it reached the office: a buyer who ticked a piece and moved on
   * sent "50" without ever considering the number, and on 2026-08-07 that
   * turned three negotiated lines of 1–2 pieces into 50/50/100 on a real lead.
   * A quantity nobody chose is worse than no quantity at all.
   */
  quantity: number | null;
  /** The buyer's customization request. Empty string means none. */
  note: string;
}
type Shortlist = Record<number, ShortlistLine>;

/** Stable identity, so the filter memo does not re-run on every render before the fetch lands. */
const EMPTY_ITEMS: SharedSelection['items'] = [];

const ShortlistLineSchema = z.object({
  quantity: z.number().int().positive().nullable(),
  note: z.string().default('')
});

function shortlistKey(token: string): string {
  return `share-shortlist:${token}`;
}

function readShortlist(token: string): Shortlist {
  try {
    const raw = localStorage.getItem(shortlistKey(token));
    if (raw === null) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return {};
    const result: Shortlist = {};
    for (const [key, value] of Object.entries(parsed)) {
      const itemNo = Number.parseInt(key, 10);
      if (!Number.isInteger(itemNo)) continue;
      // Legacy: a bare quantity, from before per-line notes existed.
      if (typeof value === 'number' && Number.isInteger(value) && value > 0) {
        result[itemNo] = { quantity: value, note: '' };
        continue;
      }
      const line = ShortlistLineSchema.safeParse(value);
      if (line.success) result[itemNo] = line.data;
    }
    return result;
  } catch {
    return {};
  }
}

function writeShortlist(token: string, shortlist: Shortlist): void {
  try {
    localStorage.setItem(shortlistKey(token), JSON.stringify(shortlist));
  } catch {
    // Private-mode/full storage: the tick just won't survive a reload. Not
    // worth surfacing to a buyer mid-browse.
  }
}

/**
 * What was sent, kept so a reload does not present an empty form as though
 * nothing had happened — the buyer would reasonably fill it in again, and the
 * office would get the same request twice. Client-side only: it settles the
 * realistic case (same phone, page reloaded) and cannot settle a second
 * device, which is why the receipt still offers a deliberate way to send more.
 */
interface SentReceipt {
  requesterName: string;
  /**
   * The rest of what they typed, so a second basket does not ask for it again.
   * Their own details, on their own device — and the lead often holds no email
   * at all, which left the repeat form with a permanently disabled button.
   */
  requesterCompany: string;
  requesterEmail: string;
  requesterPhone: string;
  items: { itemNo: number; name: string; quantity: number; note: string | null }[];
}

const SentReceiptSchema = z.object({
  requesterName: z.string(),
  // Defaulted: receipts written before repeat requests existed carry only a name.
  requesterCompany: z.string().default(''),
  requesterEmail: z.string().default(''),
  requesterPhone: z.string().default(''),
  items: z.array(
    z.object({ itemNo: z.number(), name: z.string(), quantity: z.number(), note: z.string().nullable().default(null) })
  )
});

function receiptKey(token: string): string {
  return `share-sent:${token}`;
}

function readReceipt(token: string): SentReceipt | null {
  try {
    const raw = localStorage.getItem(receiptKey(token));
    if (raw === null) return null;
    const parsed = SentReceiptSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

function writeReceipt(token: string, receipt: SentReceipt | null): void {
  try {
    if (receipt === null) localStorage.removeItem(receiptKey(token));
    else localStorage.setItem(receiptKey(token), JSON.stringify(receipt));
  } catch {
    // Private mode / full storage. The receipt just won't survive a reload.
  }
}

function PageHeader() {
  return (
    <header className="flex flex-col items-center gap-3 p-6 pb-0">
      <img src={logoUrl} alt="The Paradise" className="h-10 w-auto" />
    </header>
  );
}

function SharedSelectionPage() {
  const { token } = Route.useParams();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [shortlist, setShortlist] = useState<Shortlist>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<SentReceipt | null>(() => readReceipt(token));
  const [search, setSearch] = useState('');
  const [categoryKey, setCategoryKey] = useState(ALL_CATEGORIES);
  const [priceBand, setPriceBand] = useState(ANY_PRICE);
  const formRef = useRef<HTMLDivElement | null>(null);
  /** Set by the tap on the bar; cleared once the scroll has settled on the form. */
  const [jumping, setJumping] = useState(false);
  /**
   * The bar is only a shortcut DOWN to the form. Once the form is on screen it
   * is chrome over the photographs, so it goes away.
   */
  const [formInView, setFormInView] = useState(false);

  // The scroll runs in an EFFECT, not in the tap handler, so React has already
  // committed the render that unmounts the sentinel and paints the rest of the
  // list. Scrolling from the handler measures the page as it was before that.
  useEffect(() => {
    if (!jumping) return;
    scrollIntoViewStable(formRef.current, { onSettled: () => setJumping(false) });
  }, [jumping]);

  useEffect(() => {
    const saved = readShortlist(token);
    void loadSelection(token).then(loaded => {
      setState(loaded);
      // Staff can edit a Selection under a link already sent, so a shortlist
      // saved on an earlier visit may name a product that is no longer in it.
      // Dropping those on load is the only place it can be caught — otherwise
      // the count reads "1 item" over an empty page, and submit sends a
      // request for something the buyer can no longer see.
      if (loaded.kind !== 'ok') {
        setShortlist(saved);
        return;
      }
      const present = new Set(loaded.data.items.map(item => item.itemNo));
      const pruned = Object.fromEntries(Object.entries(saved).filter(([itemNo]) => present.has(Number(itemNo))));
      setShortlist(pruned);
      if (Object.keys(pruned).length !== Object.keys(saved).length) writeShortlist(token, pruned);
    });
  }, [token]);

  const toggle = (itemNo: number, checked: boolean) => {
    setShortlist(prev => {
      const next = { ...prev };
      if (checked) {
        // No default quantity, deliberately — see `ShortlistLine.quantity`.
        next[itemNo] = prev[itemNo] ?? { quantity: null, note: '' };
      } else {
        delete next[itemNo];
      }
      writeShortlist(token, next);
      return next;
    });
  };

  const setQuantity = (itemNo: number, quantity: number | null) => {
    setShortlist(prev => {
      const line = prev[itemNo];
      if (line === undefined) return prev;
      const next = { ...prev, [itemNo]: { ...line, quantity } };
      writeShortlist(token, next);
      return next;
    });
  };

  const setNote = (itemNo: number, note: string) => {
    setShortlist(prev => {
      const line = prev[itemNo];
      if (line === undefined) return prev;
      const next = { ...prev, [itemNo]: { ...line, note } };
      writeShortlist(token, next);
      return next;
    });
  };

  const submitRequest = async (details: RequesterDetails, nameForItem: (itemNo: number) => string) => {
    // The form's button is already disabled while anything is unanswered; this
    // is the guard that makes it true rather than merely displayed.
    const lines: { itemNo: number; quantity: number; note: string | null }[] = [];
    for (const [itemNo, line] of Object.entries(shortlist)) {
      if (line.quantity === null) {
        setSubmitError('Please say how many you need for every piece you have ticked.');
        return;
      }
      lines.push({
        itemNo: Number.parseInt(itemNo, 10),
        quantity: line.quantity,
        note: line.note.trim() === '' ? null : line.note.trim()
      });
    }

    setSubmitting(true);
    setSubmitError(null);
    const input: CreateQuoteRequestInput = { ...details, items: lines };
    try {
      const response = await fetch(`/api/share/${encodeURIComponent(token)}/quote-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input)
      });
      if (response.ok) {
        const sent: SentReceipt = {
          requesterName: details.requesterName,
          requesterCompany: details.requesterCompany,
          requesterEmail: details.requesterEmail,
          requesterPhone: details.requesterPhone,
          items: lines.map(line => ({ ...line, name: nameForItem(line.itemNo) }))
        };
        writeReceipt(token, sent);
        setReceipt(sent);
        // The basket is emptied on send. Leaving the ticks in place claimed the
        // pieces were still pending, so a buyer returning after an accepted
        // quotation saw four items ticked, added two, and would have re-asked
        // for all six — a duplicate request the office then has to untangle.
        // The receipt above is the record of what was asked.
        writeShortlist(token, {});
        setShortlist({});
        return;
      }
      const body: unknown = await response.json().catch(() => null);
      const envelope = ErrorEnvelope.safeParse(body);
      setSubmitError(envelope.success ? envelope.data.error.message : 'Could not send the request. Please try again.');
    } catch {
      setSubmitError('Could not reach the server. Check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Filtering and windowing sit ABOVE the early returns — rules of hooks. They
  // read an empty list until the fetch lands, which costs nothing.
  const allItems = state.kind === 'ok' ? state.data.items : EMPTY_ITEMS;
  const query = search.trim().toLowerCase();
  const filteredItems = useMemo(
    () =>
      allItems.filter(item => {
        if (categoryKey === SELECTED_ONLY) return item.itemNo in shortlist;
        if (categoryKey !== ALL_CATEGORIES && item.categoryName !== categoryKey) return false;
        if (priceBand !== ANY_PRICE && priceBandOf(item) !== priceBand) return false;
        return matchesSearch(item, query);
      }),
    [allItems, categoryKey, priceBand, query, shortlist]
  );
  const {
    visible: visibleItems,
    hasMore,
    sentinelRef,
    showAll
  } = useIncrementalList(filteredItems, PAGE_SIZE, `${categoryKey}|${priceBand}|${query}`);

  useEffect(() => {
    const node = formRef.current;
    if (node === null) return;
    const observer = new IntersectionObserver(entries => setFormInView(entries.some(entry => entry.isIntersecting)));
    observer.observe(node);
    return () => observer.disconnect();
    // The ref is only attached once the Selection has loaded, so the observer
    // has to be (re)attached when it does.
  }, [state.kind]);

  if (state.kind === 'loading') {
    return (
      <main className="flex min-h-dvh flex-col items-center justify-center gap-3 p-6">
        <Spinner className="size-6" />
      </main>
    );
  }

  if (state.kind === 'expired') {
    return (
      <main className="flex min-h-dvh flex-col items-center justify-center p-6">
        <Empty>
          <EmptyHeader>
            <EmptyTitle>This link has expired</EmptyTitle>
            <EmptyDescription>Please ask your contact at The Paradise for a fresh link.</EmptyDescription>
          </EmptyHeader>
        </Empty>
      </main>
    );
  }

  if (state.kind === 'revoked') {
    return (
      <main className="flex min-h-dvh flex-col items-center justify-center p-6">
        <Empty>
          <EmptyHeader>
            <EmptyTitle>This link is no longer available</EmptyTitle>
            <EmptyDescription>Please contact The Paradise if you need these details again.</EmptyDescription>
          </EmptyHeader>
        </Empty>
      </main>
    );
  }

  if (state.kind === 'error') {
    return (
      <main className="flex min-h-dvh flex-col items-center justify-center p-6">
        <Empty>
          <EmptyHeader>
            <EmptyTitle>Could not load</EmptyTitle>
            <EmptyDescription>Something went wrong. Please reload the page or try again shortly.</EmptyDescription>
          </EmptyHeader>
        </Empty>
      </main>
    );
  }

  const { data } = state;
  const itemCount = Object.keys(shortlist).length;
  /** Ticked pieces the buyer has not given a quantity for — the form cannot send while any remain. */
  const missingQuantities = Object.values(shortlist).filter(line => line.quantity === null).length;
  // Names for the receipt: the wire request carries only itemNo + quantity, and
  // the receipt has to read back to the buyer in the words they saw.
  const nameByItemNo = new Map(data.items.map(item => [item.itemNo, item.name]));

  return (
    <main className="flex min-h-dvh flex-col gap-6 pb-10">
      <PageHeader />

      <div className="flex flex-col gap-1 px-6">
        <div className="text-[10px] font-medium tracking-wider text-primary uppercase">Prepared for</div>
        <h1 className="font-heading text-xl font-semibold">{data.recipientCompany}</h1>
        {data.recipientName && <p className="text-sm text-muted-foreground">{data.recipientName}</p>}
        <p className="mt-1 text-xs text-muted-foreground">
          {data.senderName ? `Sent by ${data.senderName} · ` : ''}Ref {data.docRef}
        </p>
        {data.expiresAt && (
          <p className="text-xs text-muted-foreground">Valid until {new Date(data.expiresAt).toLocaleDateString()}</p>
        )}
      </div>

      {data.items.length > 4 && (
        <div className="px-6">
          <SharedItemFilters
            items={data.items}
            search={search}
            onSearchChange={setSearch}
            categoryKey={categoryKey}
            onCategoryChange={setCategoryKey}
            selectedCount={itemCount}
            priceMode={data.priceMode}
            priceBand={priceBand}
            onPriceBandChange={setPriceBand}
          />
        </div>
      )}

      <div className="flex flex-col gap-3 px-6">
        {visibleItems.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nothing here matches that. Clear the search to see all {data.items.length} pieces.
          </p>
        ) : (
          visibleItems.map(item => (
            <SharedItemCard
              key={item.itemNo}
              item={item}
              token={token}
              mediaBase="/api/share"
              mode="quote-request"
              checked={item.itemNo in shortlist}
              quantity={shortlist[item.itemNo]?.quantity ?? null}
              note={shortlist[item.itemNo]?.note ?? ''}
              onCheckedChange={checked => toggle(item.itemNo, checked)}
              onQuantityChange={quantity => setQuantity(item.itemNo, quantity)}
              onNoteChange={note => setNote(item.itemNo, note)}
            />
          ))
        )}
        {/* Sentinel below the last card; the observer pulls the next page as it
            nears the viewport, so scrolling never stalls at a page boundary. */}
        {hasMore && <div ref={sentinelRef} aria-hidden="true" className="h-px" />}
      </div>

      {/* Fixed and not editable, unlike a Quotation's. Shown only where prices
          are — "GST extra" under a page carrying no figures says nothing. */}
      {data.priceMode !== 'NONE' && (
        <div className="px-6">
          <DocumentTerms terms={DEFAULT_QUOTATION_TERMS} />
        </div>
      )}

      <div className="px-6" ref={formRef}>
        <Card>
          <CardContent className="flex flex-col gap-4">
            {/* The receipt is what was ALREADY sent, and it stays visible while a
                new basket is being built — a buyer adding two pieces after an
                accepted quotation should still see what they asked for before. */}
            {receipt !== null && (
              <div className="flex flex-col gap-3">
                <h2 className="font-heading text-base font-semibold">Your request has been sent</h2>
                <p className="text-sm">
                  Thank you, {receipt.requesterName}. {data.senderName ?? 'The Paradise'} will get back to you with
                  prices shortly.
                </p>
                <div className="rounded-lg border bg-muted/40 p-3">
                  <div className="text-[10px] font-medium tracking-wider text-primary uppercase">You asked about</div>
                  <ul className="mt-1.5 flex flex-col gap-0.5">
                    {receipt.items.map(line => (
                      <li key={line.itemNo} className="text-sm">
                        {line.name} <span className="text-muted-foreground">#{line.itemNo}</span> × {line.quantity}
                        {line.note !== null && (
                          <span className="block text-xs text-muted-foreground">You asked for: {line.note}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
                {itemCount === 0 && (
                  <p className="text-sm text-muted-foreground">
                    Want to ask about something else? Add it above and this form comes back.
                  </p>
                )}
              </div>
            )}

            {/* Shown whenever there is a basket to send, receipt or not. It used
                to be hidden behind an "Ask about something else" link, so a
                buyer who ticked two more pieces after ordering saw no count, no
                button and no way through. */}
            {(receipt === null || itemCount > 0) && (
              <div className="flex flex-col gap-4">
                <h2 className="font-heading text-base font-semibold">
                  {receipt === null ? 'Ask us for a price' : 'Ask about these as well'}
                </h2>
                <QuoteRequestForm
                  itemCount={itemCount}
                  defaults={{
                    // What they typed last time beats what the lead holds —
                    // they corrected it once already.
                    name: receipt?.requesterName ?? data.recipientName ?? '',
                    company: receipt?.requesterCompany || data.recipientCompany,
                    email: receipt?.requesterEmail || data.recipientEmail || '',
                    phone: receipt?.requesterPhone || data.recipientPhone || ''
                  }}
                  submitting={submitting}
                  blockedMessage={
                    missingQuantities === 0
                      ? null
                      : `Tell us how many you need for ${missingQuantities === 1 ? 'the piece' : `the ${String(missingQuantities)} pieces`} still waiting for a quantity.`
                  }
                  error={submitError}
                  onSubmit={details => void submitRequest(details, itemNo => nameByItemNo.get(itemNo) ?? `#${itemNo}`)}
                />
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* The jump paints the REST of the list first, so the form it scrolls to
          is the actual end of the page. Left windowed, the form sat just below
          the last loaded page and the pages landing behind it pushed it down
          again — one tap moved the buyer a page, not to the form. */}
      {!formInView && (
        <ShortlistBar
          count={itemCount}
          onJumpToForm={() => {
            showAll();
            setJumping(true);
          }}
        />
      )}
    </main>
  );
}
